import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { TEMPLATE_FORMS } from "../constants/templateForms";
import type { TemplateFormSubmission } from "../components/templateForm/TemplateFormDialog";
import {
  saveTemplateForm,
  TemplateFormSaveError,
  type TemplateFormSaveResult,
} from "../services/templateFormSave";

/** The saved document, handed to the signing dialog when one was asked for. */
export interface SignableDocument {
  id: number;
  filename: string;
  size: number;
}

/** Where a filed document lands. Chosen per save, because the Timeline and
 *  List views ask for it and the folder view already knows. */
export interface SaveDestination {
  projectId: string;
  folderId: number | null;
}

/**
 * A saved document waiting to be signed.
 *
 * Only the document travels: the signing dialog reads where it is filed from
 * the row itself, so a destination carried alongside could only ever agree with
 * it or lie about it.
 */
export interface PendingSigning {
  document: SignableDocument;
}

interface UseTemplateFormSaveArgs {
  buildingId: string;
  organisation: string;
  /** Closes the form once its contents are safely filed. */
  onSaved: () => void;
}

/**
 * Files a filled template form and reports what happened.
 *
 * The document list is invalidated on success so the new document appears
 * without a reload, and a form that asked to be signed hands its document to
 * the existing signing dialog rather than a second signing path.
 */
export function useTemplateFormSave({
  buildingId,
  organisation,
  onSaved,
}: UseTemplateFormSaveArgs) {
  const { t } = useLanguage();
  const copy = t.documents.templateForm;
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const queryClient = useQueryClient();

  const [saving, setSaving] = useState(false);
  const [toSign, setToSign] = useState<PendingSigning | null>(null);

  const reportNotAppended = useCallback(
    (result: TemplateFormSaveResult) => {
      toast.success(copy.saved.title, {
        description: copy.saved.description.replace("{name}", () => result.filename),
      });
      // The tick box says attachments go into the PDF; where a file could not be
      // drawn onto a page, say so instead of letting it read as done.
      if (result.notAppended.length > 0) {
        toast.warning(
          copy.saved.attachmentsNotEmbedded.replace("{names}", () => result.notAppended.join(", "))
        );
      }
    },
    [copy]
  );

  const save = useCallback(
    async (submission: TemplateFormSubmission, destination: SaveDestination) => {
      // Returning silently here reads as a broken button: nothing files, no
      // spinner, no reason. Every other refusal in this flow is toasted.
      if (!currentOrganization?.id || !userProfile?.id) {
        toast.error(copy.validation.notReady);
        return;
      }
      setSaving(true);
      try {
        const result = await saveTemplateForm({
          templateId: submission.templateId,
          form: TEMPLATE_FORMS[submission.templateId],
          copy,
          values: submission.values,
          documentName: submission.documentName,
          nameFollowsNumber: submission.nameFollowsNumber,
          attachmentsInPdf: submission.attachmentsInPdf,
          pdfLocale: submission.pdfLocale,
          signDigitally: submission.signDigitally,
          organisation,
          orgId: currentOrganization.id,
          buildingId: Number(buildingId),
          projectId: Number(destination.projectId),
          folderId: destination.folderId,
          userId: Number(userProfile.id),
        });

        // The visible document list is an MST store, reloaded by the host
        // through `onSaved` below — this only drops the react-query caches
        // that also describe the project's documents (the attachment picker's
        // list, the folder counts), so they do not serve a stale answer.
        await queryClient.invalidateQueries({ queryKey: ["template-form-documents"] });
        await queryClient.invalidateQueries({ queryKey: ["template-forms"] });
        await queryClient.invalidateQueries({ queryKey: ["documents"] });
        reportNotAppended(result);
        onSaved();

        if (submission.signDigitally) {
          setToSign({
            document: { id: result.documentId, filename: result.filename, size: result.size },
          });
          toast.info(copy.signing.startTitle, { description: copy.signing.startDescription });
        }
      } catch (error) {
        const failure = error instanceof TemplateFormSaveError ? error : null;
        // The step's copy says what did not happen; `detail` says why, where
        // the step alone would send the user round a loop that cannot end
        // (retrying a permission error, say). Backend messages are English
        // only — an untranslated clue beats a translated dead end.
        toast.error(copy.saveFailed.title, {
          description: failure
            ? [copy.saveFailed.steps[failure.step], failure.detail].filter(Boolean).join(" ")
            : String(error),
        });
      } finally {
        setSaving(false);
      }
    },
    [
      buildingId,
      copy,
      currentOrganization,
      onSaved,
      organisation,
      queryClient,
      reportNotAppended,
      userProfile,
    ]
  );

  return { save, saving, toSign, clearToSign: () => setToSign(null) };
}
